import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Button from '../Button/Button';
import styles from './ImageCropper.module.css';

type CropSize = { width: number; height: number };
type Pan = { x: number; y: number };

interface ImageCropperProps {
  file: File;
  aspectRatio: string; // e.g. "16:9", "1:1", "3:1"
  onCancel: () => void;
  onSubmit: (edited: File, previewUrl: string) => void;
  getTranslation?: (key: string) => string;
}

function toCssAspectRatio(input: string): string {
  const cleaned = (input || '').trim();
  const match = cleaned.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (!match) return '1 / 1';
  return `${match[1]} / ${match[2]}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function getFileBaseName(name: string) {
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return name;
  return name.slice(0, idx);
}

function guessExtensionFromMime(mime: string) {
  const m = (mime || '').toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('webp')) return 'webp';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  return 'jpg';
}

async function loadImageForCanvas(file: File): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  // Prefer createImageBitmap for speed/huge images.
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file);
      return { source: bmp, width: bmp.width, height: bmp.height };
    } catch {
      // Fall through to HTMLImageElement.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Failed to load image'));
      i.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to export canvas'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function normalizeRotationDeg(deg: number) {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

function getRotationLabel(rotation: number) {
  const r = normalizeRotationDeg(rotation);
  return `${r}°`;
}

function degToRad(deg: number) {
  return (deg * Math.PI) / 180;
}

type Point = { x: number; y: number };
type HalfPlane = { a: number; b: number; c: number }; // a*x + b*y <= c

function clipPolygonWithHalfPlane(poly: Point[], hp: HalfPlane): Point[] {
  if (poly.length === 0) return [];
  const out: Point[] = [];
  const inside = (p: Point) => hp.a * p.x + hp.b * p.y <= hp.c + 1e-6;
  const intersect = (p1: Point, p2: Point): Point => {
    const d1 = hp.a * p1.x + hp.b * p1.y - hp.c;
    const d2 = hp.a * p2.x + hp.b * p2.y - hp.c;
    const t = d1 / (d1 - d2);
    return { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t };
  };

  for (let i = 0; i < poly.length; i++) {
    const curr = poly[i];
    const next = poly[(i + 1) % poly.length];
    const currIn = inside(curr);
    const nextIn = inside(next);
    if (currIn && nextIn) {
      out.push(next);
    } else if (currIn && !nextIn) {
      out.push(intersect(curr, next));
    } else if (!currIn && nextIn) {
      out.push(intersect(curr, next));
      out.push(next);
    }
  }
  return out;
}

function pointInConvexPolygon(p: Point, poly: Point[]): boolean {
  if (poly.length < 3) return false;
  let sign = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    if (Math.abs(cross) < 1e-8) continue;
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (sign !== s) return false;
  }
  return true;
}

function closestPointOnSegment(p: Point, a: Point, b: Point): Point {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const abLen2 = abx * abx + aby * aby;
  if (abLen2 === 0) return a;
  const t = clamp((apx * abx + apy * aby) / abLen2, 0, 1);
  return { x: a.x + abx * t, y: a.y + aby * t };
}

function projectPointToPolygon(p: Point, poly: Point[]): Point {
  if (poly.length === 0) return p;
  if (pointInConvexPolygon(p, poly)) return p;
  let best = poly[0];
  let bestD2 = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const q = closestPointOnSegment(p, a, b);
    const dx = p.x - q.x;
    const dy = p.y - q.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = q;
    }
  }
  return best;
}

function getFrameCorners(frameW: number, frameH: number): Point[] {
  const hx = frameW / 2;
  const hy = frameH / 2;
  return [
    { x: -hx, y: -hy },
    { x: hx, y: -hy },
    { x: hx, y: hy },
    { x: -hx, y: hy },
  ];
}

function computeMinCoverScale(params: {
  frameW: number;
  frameH: number;
  imgW: number;
  imgH: number;
  rotationDeg: number;
}): number {
  const { frameW, frameH, imgW, imgH, rotationDeg } = params;
  if (!frameW || !frameH || !imgW || !imgH) return 1;

  const theta = degToRad(rotationDeg);
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const corners = getFrameCorners(frameW, frameH);
  let minScale = 0;
  for (const p of corners) {
    const u = Math.abs(c * p.x + s * p.y); // row1·p
    const v = Math.abs(-s * p.x + c * p.y); // row2·p
    minScale = Math.max(minScale, (2 * u) / imgW, (2 * v) / imgH);
  }
  // Guard against extreme values.
  return Math.max(1e-6, minScale);
}

function buildFeasiblePanPolygon(params: {
  frameW: number;
  frameH: number;
  imgW: number;
  imgH: number;
  rotationDeg: number;
  scale: number; // CSS px per image px
}): Point[] {
  const { frameW, frameH, imgW, imgH, rotationDeg, scale } = params;
  if (!frameW || !frameH || !imgW || !imgH || !scale) return [];

  const theta = degToRad(rotationDeg);
  const c = Math.cos(theta);
  const s = Math.sin(theta);

  const W = (imgW / 2) * scale;
  const H = (imgH / 2) * scale;
  const corners = getFrameCorners(frameW, frameH);

  // Start with a large bounding square, then clip by all half-planes.
  const M = Math.max(imgW * scale, imgH * scale, frameW, frameH) * 2 + 100;
  let poly: Point[] = [
    { x: -M, y: -M },
    { x: M, y: -M },
    { x: M, y: M },
    { x: -M, y: M },
  ];

  for (const p of corners) {
    const row1p = c * p.x + s * p.y;
    const row2p = -s * p.x + c * p.y;

    // |row1·(p - pan)| <= W
    // 1)  c*pan.x + s*pan.y <= W + row1p
    poly = clipPolygonWithHalfPlane(poly, { a: c, b: s, c: W + row1p });
    // 2) -c*pan.x - s*pan.y <= W - row1p
    poly = clipPolygonWithHalfPlane(poly, { a: -c, b: -s, c: W - row1p });

    // |row2·(p - pan)| <= H, row2=[-s, c]
    // 3)  s*pan.x - c*pan.y <= H - row2p
    poly = clipPolygonWithHalfPlane(poly, { a: s, b: -c, c: H - row2p });
    // 4) -s*pan.x + c*pan.y <= H + row2p
    poly = clipPolygonWithHalfPlane(poly, { a: -s, b: c, c: H + row2p });

    if (poly.length === 0) break;
  }

  return poly;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  file,
  aspectRatio,
  onCancel,
  onSubmit,
  getTranslation,
}) => {
  const t = (key: string, fallback: string) => (getTranslation ? getTranslation(key) || fallback : fallback);

  const cssAspectRatio = useMemo(() => toCssAspectRatio(aspectRatio), [aspectRatio]);

  const cropFrameRef = useRef<HTMLDivElement>(null);
  const imageSourceRef = useRef<CanvasImageSource | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  const [cropSize, setCropSize] = useState<CropSize>({ width: 0, height: 0 });
  const [rotation, setRotation] = useState(0); // degrees, smooth (0..360)
  const [zoom, setZoom] = useState(1); // 1..3
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minCoverScale = useMemo(() => {
    return computeMinCoverScale({
      frameW: cropSize.width,
      frameH: cropSize.height,
      imgW: imageSize.width,
      imgH: imageSize.height,
      rotationDeg: rotation,
    });
  }, [cropSize.height, cropSize.width, imageSize.height, imageSize.width, rotation]);

  const effectiveScale = useMemo(() => {
    if (!minCoverScale || !isFinite(minCoverScale)) return null;
    return minCoverScale * zoom;
  }, [minCoverScale, zoom]);

  const displaySize = useMemo(() => {
    if (!effectiveScale) return { width: 0, height: 0 };
    return {
      width: imageSize.width * effectiveScale,
      height: imageSize.height * effectiveScale,
    };
  }, [effectiveScale, imageSize.height, imageSize.width]);

  const feasiblePanPoly = useMemo(() => {
    if (!effectiveScale) return [];
    return buildFeasiblePanPolygon({
      frameW: cropSize.width,
      frameH: cropSize.height,
      imgW: imageSize.width,
      imgH: imageSize.height,
      rotationDeg: rotation,
      scale: effectiveScale,
    });
  }, [cropSize.height, cropSize.width, effectiveScale, imageSize.height, imageSize.width, rotation]);

  const clampPan = useCallback((p: Pan): Pan => {
    const projected = projectPointToPolygon(p, feasiblePanPoly);
    return { x: projected.x, y: projected.y };
  }, [feasiblePanPoly]);

  // Keep pan clamped when zoom/crop size changes.
  useEffect(() => {
    setPan((prev) => clampPan(prev));
  }, [clampPan]);

  // Track crop-frame size.
  useLayoutEffect(() => {
    const el = cropFrameRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setCropSize({ width: rect.width, height: rect.height });
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Prepare preview URL + a CanvasImageSource for export.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsPreparing(true);
      try {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }

        const url = URL.createObjectURL(file);
        previewUrlRef.current = url;
        setPreviewUrl(url);

        const { source, width, height } = await loadImageForCanvas(file);
        if (cancelled) return;
        imageSourceRef.current = source;
        setImageSize({ width, height });
      } catch (e) {
        if (!cancelled) {
          imageSourceRef.current = null;
          setImageSize({ width: 0, height: 0 });
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) setIsPreparing(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [file]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (isPreparing || isSubmitting) return;
    if (!cropFrameRef.current) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  }, [isPreparing, isSubmitting, pan.x, pan.y]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const next = clampPan({
      x: d.startPanX + (e.clientX - d.startClientX),
      y: d.startPanY + (e.clientY - d.startClientY),
    });
    setPan(next);
  }, [clampPan]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  }, []);


  const handleZoomChange = useCallback((value: number) => {
    setZoom(value);
  }, []);

  const handleRotationSliderChange = useCallback((value: number) => {
    setRotation(value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isPreparing || isSubmitting) return;
    const source = imageSourceRef.current;
    if (!source) return;
    if (!cropSize.width || !cropSize.height) return;
    if (!imageSize.width || !imageSize.height) return;
    if (!effectiveScale) return;

    setIsSubmitting(true);
    try {
      const s = effectiveScale;
      const cropWpx = cropSize.width / s;
      const cropHpx = cropSize.height / s;
      
      // Calculate minimum export size to ensure good quality when displayed
      // For 16:9 banners, ensure at least 1920x1080; for others, scale crop frame by 2-3x for retina
      const minExportWidth = cssAspectRatio === '16 / 9' ? 1920 : Math.max(1920, cropSize.width * 2.5);
      const minExportHeight = cssAspectRatio === '16 / 9' ? 1080 : Math.max(1080, cropSize.height * 2.5);
      
      // Calculate scale factor to reach minimum export size
      const scaleToMin = Math.max(
        minExportWidth / cropWpx,
        minExportHeight / cropHpx
      );
      
      // Use the larger of: actual crop size or minimum export size
      const exportW = Math.max(minExportWidth, Math.round(cropWpx * scaleToMin));
      const exportH = Math.max(minExportHeight, Math.round(cropHpx * scaleToMin));
      
      const outCanvas = document.createElement('canvas');
      outCanvas.width = exportW;
      outCanvas.height = exportH;
      const outCtx = outCanvas.getContext('2d');
      if (!outCtx) throw new Error('Canvas 2D context not available');

      outCtx.imageSmoothingEnabled = true;
      outCtx.imageSmoothingQuality = 'high';
      outCtx.save();
      // Scale the crop area to match export size
      outCtx.translate(exportW / 2, exportH / 2);
      outCtx.scale(exportW / cropWpx, exportH / cropHpx);
      outCtx.translate(pan.x / s, pan.y / s);
      outCtx.rotate(degToRad(rotation));
      outCtx.drawImage(source, -imageSize.width / 2, -imageSize.height / 2);
      outCtx.restore();

      const type = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
      const quality = type.includes('jpeg') || type.includes('jpg') || type.includes('webp') ? 0.92 : undefined;
      const blob = await canvasToBlob(outCanvas, type, quality);

      const ext = guessExtensionFromMime(type);
      const nextName = `${getFileBaseName(file.name)}-cropped.${ext}`;
      const edited = new File([blob], nextName, { type });

      const previewUrl = URL.createObjectURL(edited);
      onSubmit(edited, previewUrl);
    } catch {
      // Export failed
    } finally {
      setIsSubmitting(false);
    }
  }, [cropSize.height, cropSize.width, cssAspectRatio, effectiveScale, file, imageSize.height, imageSize.width, isPreparing, isSubmitting, onSubmit, pan.x, pan.y, rotation]);

  const frameStyle = useMemo(() => {
    // Use CSS aspect-ratio for stable layout.
    return { aspectRatio: cssAspectRatio } as React.CSSProperties;
  }, [cssAspectRatio]);

  return (
    <div className={styles.cropper}>
      <div className={styles.stage}>
        <div
          ref={cropFrameRef}
          className={styles.cropFrame}
          style={frameStyle}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          role="application"
          aria-label={t('imageCropper.cropArea', 'Crop area')}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={t('imageCropper.imagePreview', 'Image preview')}
              className={styles.image}
              style={{
                width: displaySize.width ? `${displaySize.width}px` : undefined,
                height: displaySize.height ? `${displaySize.height}px` : undefined,
                transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) rotate(${normalizeRotationDeg(rotation)}deg)`,
              }}
              draggable={false}
            />
          ) : (
            <div className={styles.placeholder}>
              {isPreparing ? t('imageCropper.preparing', 'Preparing...') : t('imageCropper.noPreview', 'No preview')}
            </div>
          )}
          <div className={styles.gridOverlay} />
          <div className={styles.frameOverlay} />
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.toolbarGroup}>
          <label className={styles.rotationSliderLabel}>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={normalizeRotationDeg(rotation)}
              onChange={(e) => handleRotationSliderChange(Number(e.target.value))}
              disabled={isPreparing || isSubmitting}
              className={styles.rotationRange}
              style={{
                '--rotation-percent': `${(normalizeRotationDeg(rotation) / 360) * 100}%`
              } as React.CSSProperties}
            />
            <span className={styles.rotationValue}>{getRotationLabel(rotation)}</span>
          </label>
          <label className={styles.zoomLabel}>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => handleZoomChange(Number(e.target.value))}
              disabled={isPreparing || isSubmitting}
              className={styles.zoomRange}
              style={{
                '--zoom-percent': `${((zoom - 1) / (3 - 1)) * 100}%`
              } as React.CSSProperties}
            />
            <span className={styles.zoomValue}>{Math.round(zoom * 100)}%</span>
          </label>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="medium"
          onClick={onCancel}
          disabled={isPreparing || isSubmitting}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={!previewUrl || isPreparing || isSubmitting}
        >
          {isSubmitting ? t('imageCropper.submitting', 'Submitting...') : t('imageCropper.submit', 'Submit')}
        </Button>
      </div>
    </div>
  );
};

export default ImageCropper;

